import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAllResume,
    getResumeByID,
    createResume,
    updateResume,
    updateResumeStatus,
    deleteResume,
    rateResume,
    getResumeRating,
    addResumeComment,
    getResumeComments,
    addResumeTag,
    getResumeTags,
} from "./thunk";
import { normalizeEntities, NormalizedState } from '../../utils/normalizeState';

interface Resume {
    id: string;
    [key: string]: any;
}

interface ResumesState {
    resumes: NormalizedState<Resume> & {
        pagination?: {
            total: number;
            page: number;
            per_page: number;
            total_pages: number;
        } | null;
    };
    selectedResume: Resume | null;
    resumeRatings: Record<string, any>;
    resumeComments: Record<string, any[]>;
    resumeTags: Record<string, any[]>;
    loading: boolean;
    error: string | null;
}

const initialState: ResumesState = {
    resumes: {
        entities: {},
        ids: [],
        loading: false,
        error: null,
        pagination: null,
    },
    selectedResume: null,
    resumeRatings: {},
    resumeComments: {},
    resumeTags: {},
    loading: false,
    error: null
};

const ResumesSlice = createSlice({
    name: 'Resumes',
    initialState,
    reducers: {
        clearSelectedResume: (state) => {
            state.selectedResume = null;
        },
        clearResumes: (state) => {
            state.resumes.entities = {};
            state.resumes.ids = [];
            state.resumes.error = null;
        },
    },
    extraReducers: (builder) => {
        // Get All Resumes - Normalized
        builder.addCase(getAllResume.pending, (state) => {
            state.resumes.loading = true;
            state.resumes.error = null;
            state.loading = true;
        });
        builder.addCase(getAllResume.fulfilled, (state, action: PayloadAction<any>) => {
            // Handle both paginated and non-paginated responses
            let resumes = [];
            const responseData = action.payload?.data || action.payload;
            
            if (Array.isArray(responseData)) {
                // Non-paginated response (backward compatible)
                resumes = responseData;
                state.resumes.pagination = null;
            } else if (responseData && responseData.resumes && Array.isArray(responseData.resumes)) {
                // Paginated response
                resumes = responseData.resumes;
                state.resumes.pagination = responseData.pagination || null;
            } else {
                // Fallback to empty array
                resumes = [];
                state.resumes.pagination = null;
            }
            
            const normalized = normalizeEntities(resumes);
            state.resumes.entities = normalized.entities;
            state.resumes.ids = normalized.ids;
            state.resumes.loading = false;
            state.resumes.error = null;
            state.resumes.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getAllResume.rejected, (state, action) => {
            state.resumes.error = action.payload as string || 'Failed to fetch resumes';
            state.resumes.loading = false;
            state.loading = false;
        });

        // Create Resume - Normalized
        builder.addCase(createResume.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createResume.fulfilled, (state, action: PayloadAction<any>) => {
            const resume = action.payload?.data || action.payload;
            if (resume?.id) {
                state.resumes.entities[resume.id] = resume;
                if (!state.resumes.ids.includes(resume.id)) {
                    state.resumes.ids.unshift(resume.id);
                }
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(createResume.rejected, (state, action) => {
            state.error = action.payload as string || 'Failed to create resume';
            state.loading = false;
        });

        // Get Resume By ID
        builder.addCase(getResumeByID.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getResumeByID.fulfilled, (state, action: PayloadAction<any>) => {
            const resume = action.payload?.data || action.payload || null;
            state.selectedResume = resume;
            if (resume?.id) {
                state.resumes.entities[resume.id] = resume;
                if (!state.resumes.ids.includes(resume.id)) {
                    state.resumes.ids.push(resume.id);
                }
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(getResumeByID.rejected, (state, action) => {
            state.error = action.payload as string || 'Failed to fetch resume';
            state.loading = false;
        });

        // Update Resume - Normalized
        builder.addCase(updateResume.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateResume.fulfilled, (state, action: PayloadAction<any>) => {
            const resume = action.payload?.data || action.payload;
            if (resume?.id && state.resumes.entities[resume.id]) {
                state.resumes.entities[resume.id] = {
                    ...state.resumes.entities[resume.id],
                    ...resume,
                };
                if (state.selectedResume?.id === resume.id) {
                    state.selectedResume = state.resumes.entities[resume.id];
                }
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateResume.rejected, (state, action) => {
            state.error = action.payload as string || 'Failed to update resume';
            state.loading = false;
        });

        // Update Resume Status - Normalized
        builder.addCase(updateResumeStatus.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateResumeStatus.fulfilled, (state, action: PayloadAction<any>) => {
            const resume = action.payload?.data || action.payload;
            if (resume?.id && state.resumes.entities[resume.id]) {
                state.resumes.entities[resume.id] = {
                    ...state.resumes.entities[resume.id],
                    ...resume,
                };
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(updateResumeStatus.rejected, (state, action) => {
            state.error = action.payload as string || 'Failed to update resume status';
            state.loading = false;
        });

        // Delete Resume - Normalized
        builder.addCase(deleteResume.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteResume.fulfilled, (state, action) => {
            // Use the ID passed to the thunk (available in action.meta.arg)
            const resumeId = String(action.meta.arg);
            
            if (resumeId) {
                delete state.resumes.entities[resumeId];
                state.resumes.ids = state.resumes.ids.filter((id: string) => id !== resumeId);
                if (state.selectedResume?.id === resumeId) {
                    state.selectedResume = null;
                }
            }
            state.loading = false;
            state.error = null;
        });
        builder.addCase(deleteResume.rejected, (state, action) => {
            state.error = action.payload as string || 'Failed to delete resume';
            state.loading = false;
        });

        // Rate Resume
        builder.addCase(rateResume.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, rating } = action.payload?.data || action.payload || {};
            if (resumeId) {
                state.resumeRatings[resumeId] = rating;
            }
        });

        // Get Resume Rating
        builder.addCase(getResumeRating.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, rating } = action.payload?.data || action.payload || {};
            if (resumeId) {
                state.resumeRatings[resumeId] = rating;
            }
        });

        // Add Resume Comment
        builder.addCase(addResumeComment.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, comment } = action.payload?.data || action.payload || {};
            if (resumeId && comment) {
                if (!state.resumeComments[resumeId]) {
                    state.resumeComments[resumeId] = [];
                }
                state.resumeComments[resumeId].push(comment);
            }
        });

        // Get Resume Comments
        builder.addCase(getResumeComments.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, comments } = action.payload?.data || action.payload || {};
            if (resumeId && comments) {
                state.resumeComments[resumeId] = comments;
            }
        });

        // Add Resume Tag
        builder.addCase(addResumeTag.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, tag } = action.payload?.data || action.payload || {};
            if (resumeId && tag) {
                if (!state.resumeTags[resumeId]) {
                    state.resumeTags[resumeId] = [];
                }
                state.resumeTags[resumeId].push(tag);
            }
        });

        // Get Resume Tags
        builder.addCase(getResumeTags.fulfilled, (state, action: PayloadAction<any>) => {
            const { resumeId, tags } = action.payload?.data || action.payload || {};
            if (resumeId && tags) {
                state.resumeTags[resumeId] = tags;
            }
        });
    },
});

export const { clearSelectedResume, clearResumes } = ResumesSlice.actions;
export default ResumesSlice.reducer;